import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  LocalHospital as HospitalIcon,
  MedicalServices as ConsultaIcon,
  MeetingRoom as PabellonIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../../services/firestoreService';
import { useBranch } from '../../context/BranchContext';

const CAPACITY_TYPES = [
  { value: 'pabellon', label: 'Pabellón', icon: <PabellonIcon /> },
  { value: 'consulta', label: 'Consulta', icon: <ConsultaIcon /> },
  { value: 'hospital', label: 'Hospital', icon: <HospitalIcon /> }
];

const WEEK_DAYS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' }
];

const ManageCapacities = () => {
  const { isAdmin, activeBranchId, accessibleBranches } = useBranch();
  const [capacities, setCapacities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCapacity, setEditingCapacity] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    type: 'pabellon',
    number: '',
    location: '',
    resources: [], // Mesas quirúrgicas, caniles/gatiles, etc.
    branchIds: [], // Sucursales asociadas
    operatingHours: {
      days: [1, 2, 3, 4, 5], // Lunes a Viernes por defecto
      startTime: '08:00',
      endTime: '20:00'
    }
  });

  useEffect(() => {
    loadCapacities();
    setBranches(accessibleBranches);
  }, [accessibleBranches]);

  const loadCapacities = async () => {
    try {
      const data = isAdmin
        ? await getCollection('capacities')
        : (await getDocs(
            query(collection(db, 'capacities'), where('branchIds', 'array-contains', activeBranchId))
          )).docs.map((d) => ({ id: d.id, ...d.data() }));
      setCapacities(data);
    } catch (error) {
      console.error('Error loading capacities:', error);
      setSnackbar({ open: true, message: 'Error al cargar capacidades', severity: 'error' });
    }
  };

  const handleOpenDialog = () => {
    setEditingCapacity(null);
    setFormData({
      type: 'pabellon',
      number: '',
      location: '',
      resources: [],
      branchIds: [],
      operatingHours: {
        days: [1, 2, 3, 4, 5],
        startTime: '08:00',
        endTime: '20:00'
      }
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCapacity(null);
    setFormData({
      type: 'pabellon',
      number: '',
      location: '',
      resources: [],
      branchIds: [],
      operatingHours: {
        days: [1, 2, 3, 4, 5],
        startTime: '08:00',
        endTime: '20:00'
      }
    });
  };

  const handleEdit = (capacity) => {
    setEditingCapacity(capacity);
    const capacityBranches = (capacity.branchIds || []).map((branchId) => branches.find((b) => b.id === branchId)).filter(Boolean);
    setFormData({
      type: capacity.type,
      number: capacity.number || '',
      location: capacity.location || '',
      resources: capacity.resources || [],
      branchIds: capacityBranches,
      operatingHours: capacity.operatingHours || {
        days: [1, 2, 3, 4, 5],
        startTime: '08:00',
        endTime: '20:00'
      }
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta capacidad?')) {
      try {
        await deleteDocument('capacities', id);
        setSnackbar({ open: true, message: 'Capacidad eliminada exitosamente', severity: 'success' });
        loadCapacities();
      } catch (error) {
        console.error('Error deleting capacity:', error);
        setSnackbar({ open: true, message: 'Error al eliminar capacidad', severity: 'error' });
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOperatingHoursChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [field]: value
      }
    }));
  };

  const handleDayToggle = (dayValue) => {
    setFormData(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        days: prev.operatingHours.days.includes(dayValue)
          ? prev.operatingHours.days.filter(d => d !== dayValue)
          : [...prev.operatingHours.days, dayValue]
      }
    }));
  };

  const calculateWeeklyCapacity = () => {
    const { days, startTime, endTime } = formData.operatingHours;
    if (!startTime || !endTime || days.length === 0) return 0;

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const startTotalMinutes = startHour * 60 + startMin;
    const endTotalMinutes = endHour * 60 + endMin;

    if (endTotalMinutes <= startTotalMinutes) return 0;

    const dailyHours = (endTotalMinutes - startTotalMinutes) / 60;
    const weeklyHours = dailyHours * days.length;

    return weeklyHours.toFixed(1);
  };

  const handleAddResource = () => {
    const resourceName = prompt(getResourcePromptText());
    if (resourceName && resourceName.trim()) {
      setFormData(prev => ({
        ...prev,
        resources: [...prev.resources, resourceName.trim()]
      }));
    }
  };

  const handleRemoveResource = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  const getResourcePromptText = () => {
    switch (formData.type) {
      case 'pabellon':
        return 'Ingrese el nombre de la mesa quirúrgica (ej: Mesa 1, Mesa Principal):';
      case 'hospital':
        return 'Ingrese el nombre del canil/gatil (ej: Canil 1, Gatil A):';
      default:
        return 'Ingrese el nombre del recurso:';
    }
  };

  const getResourceLabel = () => {
    switch (formData.type) {
      case 'pabellon':
        return 'Mesas Quirúrgicas';
      case 'hospital':
        return 'Caniles/Gatiles';
      default:
        return 'Recursos';
    }
  };

  const handleSave = async () => {
    // Validaciones
    if (!formData.location.trim()) {
      setSnackbar({ open: true, message: 'El lugar/unidad es obligatorio', severity: 'warning' });
      return;
    }

    if ((formData.type === 'pabellon' || formData.type === 'consulta') && !formData.number.trim()) {
      setSnackbar({ open: true, message: 'El número es obligatorio', severity: 'warning' });
      return;
    }

    if (formData.operatingHours.days.length === 0) {
      setSnackbar({ open: true, message: 'Debe seleccionar al menos un día de funcionamiento', severity: 'warning' });
      return;
    }

    try {
      const selectedBranchIds = formData.branchIds.map((b) => b.id);
      const capacityData = {
        type: formData.type,
        number: formData.number.trim(),
        location: formData.location.trim(),
        resources: formData.resources,
        branchIds: isAdmin ? selectedBranchIds : (selectedBranchIds.length ? selectedBranchIds : [activeBranchId]),
        operatingHours: formData.operatingHours,
        weeklyCapacity: calculateWeeklyCapacity(),
        createdAt: editingCapacity ? editingCapacity.createdAt : new Date(),
        updatedAt: new Date()
      };

      if (editingCapacity) {
        await updateDocument('capacities', editingCapacity.id, capacityData);
        setSnackbar({ open: true, message: 'Capacidad actualizada exitosamente', severity: 'success' });
      } else {
        await addDocument('capacities', capacityData);
        setSnackbar({ open: true, message: 'Capacidad agregada exitosamente', severity: 'success' });
      }

      handleCloseDialog();
      loadCapacities();
    } catch (error) {
      console.error('Error saving capacity:', error);
      setSnackbar({ open: true, message: 'Error al guardar capacidad', severity: 'error' });
    }
  };

  const getTypeIcon = (type) => {
    const typeConfig = CAPACITY_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.icon : <BusinessIcon />;
  };

  const getTypeLabel = (type) => {
    const typeConfig = CAPACITY_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.label : type;
  };

  const groupedCapacities = capacities.reduce((acc, capacity) => {
    if (!acc[capacity.type]) {
      acc[capacity.type] = [];
    }
    acc[capacity.type].push(capacity);
    return acc;
  }, {});

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Gestión de Capacidades
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure los pabellones, consultas y capacidad hospitalaria
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Nueva Capacidad
        </Button>
      </Box>
      {/* Resumen por tipo */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {CAPACITY_TYPES.map(type => {
          const count = capacities.filter(c => c.type === type.value).length;
          return (
            <Grid
              key={type.value}
              size={{
                xs: 12,
                sm: 4
              }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    {type.icon}
                    <Typography variant="h6">{type.label}</Typography>
                  </Box>
                  <Typography variant="h3" color="primary">
                    {count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {count === 1 ? 'capacidad registrada' : 'capacidades registradas'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {/* Lista de capacidades agrupadas por tipo */}
      {Object.keys(groupedCapacities).map(type => (
        <Box key={type} sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {getTypeIcon(type)}
            {getTypeLabel(type)}
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {(type === 'pabellon' || type === 'consulta') && (
                    <TableCell><strong>Número</strong></TableCell>
                  )}
                  <TableCell><strong>Lugar/Unidad</strong></TableCell>
                  <TableCell><strong>Sucursales</strong></TableCell>
                  <TableCell><strong>Horario</strong></TableCell>
                  <TableCell><strong>Capacidad Semanal</strong></TableCell>
                  <TableCell><strong>Recursos</strong></TableCell>
                  <TableCell align="right"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedCapacities[type].map((capacity) => (
                  <TableRow key={capacity.id}>
                    {(type === 'pabellon' || type === 'consulta') && (
                      <TableCell>{capacity.number}</TableCell>
                    )}
                    <TableCell>{capacity.location}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {capacity.branchIds && capacity.branchIds.length > 0 ? (
                          capacity.branchIds.map((branchId) => {
                            const branch = branches.find((b) => b.id === branchId);
                            return branch ? (
                              <Chip key={branchId} label={branch.name} size="small" variant="outlined" />
                            ) : null;
                          })
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Sin sucursales
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {capacity.operatingHours ? (
                        <Typography variant="body2">
                          {capacity.operatingHours.days.map(d => WEEK_DAYS.find(w => w.value === d)?.label).join(', ')}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {capacity.operatingHours.startTime} - {capacity.operatingHours.endTime}
                          </Typography>
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Sin horario
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {capacity.weeklyCapacity ? `${capacity.weeklyCapacity} hrs` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {capacity.resources && capacity.resources.length > 0 ? (
                          capacity.resources.map((resource, idx) => (
                            <Chip key={idx} label={resource} size="small" />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Sin recursos
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(capacity)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(capacity.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
      {capacities.length === 0 && (
        <Alert severity="info">
          No hay capacidades registradas. Haga clic en "Nueva Capacidad" para agregar una.
        </Alert>
      )}
      {/* Dialog para agregar/editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCapacity ? 'Editar Capacidad' : 'Nueva Capacidad'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de Capacidad *"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  disabled={!!editingCapacity}
                >
                  {CAPACITY_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {(formData.type === 'pabellon' || formData.type === 'consulta') && (
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <TextField
                    fullWidth
                    label={`Número de ${formData.type === 'pabellon' ? 'Pabellón' : 'Consulta'} *`}
                    name="number"
                    value={formData.number}
                    onChange={handleInputChange}
                    placeholder="Ej: 1, 2, A, B"
                  />
                </Grid>
              )}

              <Grid
                size={{
                  xs: 12,
                  sm: formData.type === 'hospital' ? 12 : 6
                }}>
                <TextField
                  fullWidth
                  label="Lugar o Unidad *"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Ej: Piso 1, Edificio A, Ala Norte"
                />
              </Grid>

              <Grid size={12}>
                <Autocomplete
                  multiple
                  fullWidth
                  value={formData.branchIds}
                  onChange={(e, value) => setFormData(prev => ({ ...prev, branchIds: value }))}
                  options={branches}
                  getOptionLabel={(b) => b.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursales (opcional)"
                      placeholder="Selecciona sucursales donde opera esta capacidad"
                    />
                  )}
                  noOptionsText="Sin sucursales"
                  clearText="Limpiar"
                />
              </Grid>

              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Horario de Funcionamiento
                </Typography>
              </Grid>

              <Grid size={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Días de funcionamiento:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {WEEK_DAYS.map((day) => (
                    <FormControlLabel
                      key={day.value}
                      control={
                        <Checkbox
                          checked={formData.operatingHours.days.includes(day.value)}
                          onChange={() => handleDayToggle(day.value)}
                        />
                      }
                      label={day.label}
                    />
                  ))}
                </Box>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Hora Inicio *"
                  type="time"
                  value={formData.operatingHours.startTime}
                  onChange={(e) => handleOperatingHoursChange('startTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Hora Fin *"
                  type="time"
                  value={formData.operatingHours.endTime}
                  onChange={(e) => handleOperatingHoursChange('endTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    Capacidad semanal estimada: <strong>{calculateWeeklyCapacity()} horas</strong>
                  </Typography>
                </Alert>
              </Grid>

              {(formData.type === 'pabellon' || formData.type === 'hospital') && (
                <Grid size={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      {getResourceLabel()}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddResource}
                    >
                      Agregar
                    </Button>
                  </Box>
                  
                  {formData.resources.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.resources.map((resource, index) => (
                        <Chip
                          key={index}
                          label={resource}
                          onDelete={() => handleRemoveResource(index)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Alert severity="info">
                      {formData.type === 'pabellon' 
                        ? 'Agregue mesas quirúrgicas disponibles en este pabellón'
                        : 'Agregue caniles/gatiles disponibles'}
                    </Alert>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editingCapacity ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageCapacities;
